/* eslint-disable react/no-multi-comp */
import {Field, Form, Formik} from 'formik';
import React from 'react';
import Spinner from '../../atoms/Spinner';
import ExpiryInput from '../../molecules/ExpiryInputField';
import FieldWrapper from '../../molecules/FieldWrapper';
import CybersourceCardNumberField from '../../molecules/CybersourceCardNumberField';
import MicroformFieldComponent from '../../molecules/MicroformField';
import CyberSourceCvvField from '../../molecules/CyberSourceCvvField';
import SubmitButton from '../../molecules/SubmitButton';
import {initMicroform, pushAnalyticsValidationEvent} from './cybersource-form.services';
import {validateToken} from './cybersource-form.validators';
import {pushAnalyticsEvent} from '../Analytics';
import config from '../../../config';
import {getYearsFromNow, handleEnterKey} from '../../../utils/helpers';
import {MONTHS, NEW_CYBERSOURCE_PAYMENT_FORM} from '../../../consts';
import ExpirySelectField from '../../molecules/ExpirySelectField';

const {features} = config;

const FEATURE_YEARS_NUMBER = 10;

// microform fields are ready a bit later then load event is triggired
const MICROFORM_RENDER_LAG = 1000;

const createOption = id => ({id, name: id});

const getPriorityError = errors => {
    if (errors.number) {
        return {
            formField: 'number',
            formMessage: errors.number
        };
    }

    if (errors.expiryMonth) {
        return {
            formField: 'expiryMonth',
            formMessage: errors.expiryMonth
        };
    }

    if (errors.expiryDate) {
        return {
            formField: 'expiryDate',
            formMessage: errors.expiryDate
        };
    }

    if (errors.securityCode) {
        return {
            formField: 'securityCode',
            formMessage: errors.securityCode
        };
    }

    return undefined;
};

const FIELDS = {
    cardNumber: {
        name: 'number',
        id: 'cardNumber-container',
        component: CybersourceCardNumberField
    },
    expiryDate: {
        name: 'expiryDate',
        id: 'expiryDate',
        placeholder: 'MM / YY',
        component: ExpiryInput
    },
    expiryMonth: {
        name: 'expiryMonth',
        placeholder: 'MM',
        options: MONTHS.map(createOption),
        component: ExpirySelectField
    },
    expiryYear: {
        name: 'expiryYear',
        placeholder: 'YYYY',
        options: getYearsFromNow(FEATURE_YEARS_NUMBER).map(createOption),
        component: ExpirySelectField
    },
    securityCode: {
        name: 'securityCode',
        id: 'securityCode-container',
        component: features.cvvHoverPopup.enabled ? CyberSourceCvvField : MicroformFieldComponent
    }
};

class CyberSourceFormWrapper extends React.Component {
    state = {
        microformLoaded: false,
        shouldSaveCard: config.features.saveCards.enabled
    };

    componentDidMount() {
        this.initializeMicroform(this.props);
    }

    componentWillReceiveProps(nextProps) {
        this.initializeMicroform(nextProps);
    }

    getExpiryCybersourceOptions(values) {
        const {featureFlags: {expiryDateDropDown}} = this.props;

        if (expiryDateDropDown.enabled) {
            return {
                expirationMonth: values[FIELDS.expiryMonth.name],
                expirationYear: values[FIELDS.expiryYear.name]
            };
        }

        const expiryRegexp = /^(\d{2})\/(\d{2})$/;
        const expiryValue = values[FIELDS.expiryDate.name];
        const [, expirationMonth, expirationYear2Digits] = expiryValue.match(expiryRegexp);
        return {
            expirationMonth,
            expirationYear: `20${expirationYear2Digits}`
        };
    }

    initializeMicroform = props => {
        if (!this.microform && props.isLoaded && !props.genericError) {
            const {
                cyberSourceData: {
                    data: {
                        captureContext
                    }
                }
            } = props;

            this.microform = initMicroform(captureContext, props.translations);

            Promise.all([Object.keys(this.microform.fields).map(fieldName => new Promise(resolve => {
                this.microform.fields[fieldName].on('load', resolve);
            }))]).then(() => {
                setTimeout(() => {
                    pushAnalyticsEvent({
                        interactionName: 'form loaded',
                        formName: NEW_CYBERSOURCE_PAYMENT_FORM
                    });
                    this.setState({
                        microformLoaded: true
                    });
                }, MICROFORM_RENDER_LAG);
            });
        }
    }

    setShouldSaveCard = () => {
        const {shouldSaveCard} = this.state;
        pushAnalyticsEvent({
            interactionName: 'card payment interaction',
            cardPaymentAction: shouldSaveCard ? 'save this card toggled off' : 'save this card toggled on'
        });
        this.setState({shouldSaveCard: !shouldSaveCard});
    }

    handleSubmit = async values => {
        if (this.props.genericError) {
            return;
        }

        pushAnalyticsEvent({
            interactionName: 'form submit',
            formName: NEW_CYBERSOURCE_PAYMENT_FORM,
            formResult: 'unknown'
        });
        const {
            startAuthorization,
            csrfToken,
            languageCode,
            availableLanguageCodes
        } = this.props;
        const shouldUseSavedCard = false;
        const {shouldSaveCard} = this.state;
        const cybersourceOptions = this.getExpiryCybersourceOptions(values);

        this.microform.createToken(
            cybersourceOptions,
            (err, cardDetailsToken) => startAuthorization(
                err,
                cardDetailsToken,
                csrfToken,
                languageCode,
                availableLanguageCodes,
                shouldUseSavedCard,
                shouldSaveCard
            )
        );
    }

    validate = async values => {
        const {
            translations: {
                cybersource
            },
            featureFlags: {expiryDateDropDown},
            cardType
        } = this.props;

        const errors = await validateToken(cybersource.errors)({
            microform: this.microform,
            values,
            cybersourceOptions: this.getExpiryCybersourceOptions(values),
            cardType,
            expiryDateDropDown
        });

        pushAnalyticsValidationEvent(getPriorityError(errors));

        return errors;
    };

    render() {
        const {microformLoaded} = this.state;
        const {
            featureFlags: {expiryDateDropDown}
        } = this.props;
        const {
            shouldSaveCard
        } = this.state;

        const initialValues = expiryDateDropDown.enabled ? {
            number: MicroformFieldComponent.getInitialFieldValues(),
            expiryMonth: '',
            expiryYear: '',
            securityCode: MicroformFieldComponent.getInitialFieldValues()
        } : {
            number: MicroformFieldComponent.getInitialFieldValues(),
            expiryDate: '',
            securityCode: MicroformFieldComponent.getInitialFieldValues()
        };

        return (
            <Formik
                initialValues={initialValues}
                onSubmit={this.handleSubmit}
                validate={this.validate}
                validateOnBlur={false}
                validateOnChange={false}
                enableReinitialize
            >
                {formikOptions => (
                    <CyberSourceForm
                        {...this.props}
                        formikOptions={formikOptions}
                        microform={this.microform}
                        microformLoaded={microformLoaded}
                        featureFlags={this.props.featureFlags}
                        setShouldSaveCard={this.setShouldSaveCard}
                        shouldSaveCard={shouldSaveCard}
                    />
                )}
            </Formik>
        );
    }
}

class CyberSourceForm extends React.Component {
    isWaitingForAnalytics = false;

    componentDidMount() {
        this.getMicroformField(FIELDS.cardNumber.name)?.on('autocomplete', this.setExpiryDateOnAutocomplete);
    }

    componentDidUpdate(prevProps) {
        const {microform} = this.props;
        if (!prevProps.microform && microform) {
            this.getMicroformField(FIELDS.cardNumber.name).on('autocomplete', this.setExpiryDateOnAutocomplete);
        }
    }

    componentWillUnmount() {
        this.getMicroformField(FIELDS.cardNumber.name)?.off('autocomplete', this.setExpiryDateOnAutocomplete);
    }

    setExpiryDateOnAutocomplete = ({expirationMonth, expirationYear}) => {
        const {formikOptions, featureFlags: {expiryDateDropDown}} = this.props;
        if (expiryDateDropDown.enabled) {
            const year = expirationYear?.length === 2 ? `20${expirationYear}` : expirationYear;
            if (expirationMonth?.length === 2 && year?.length === 4) {
                formikOptions.setFieldValue(FIELDS.expiryMonth.name, expirationMonth);
                formikOptions.setFieldValue(FIELDS.expiryYear.name, year);
            }
        } else {
            const year = expirationYear?.length === 4 ? expirationYear.substr(2) : expirationYear;
            if (expirationMonth?.length === 2 && year?.length === 2) {
                formikOptions.setFieldValue(FIELDS.expiryDate.name, `${expirationMonth}/${year}`);
            }
        }
    }

    getMicroformField = name => this.props.microform?.fields[name]

    getSubmitButtonText() {
        const {
            isWebRelaunchStyleEnabled,
            calculation,
            translations: {
                cybersource: {
                    submitButton
                }
            }
        } = this.props;

        if (!isWebRelaunchStyleEnabled || !calculation?.price) {
            return submitButton;
        }

        const {price, discount = 0} = calculation;
        const {sendAmount = 0, fees = 0, sendCurrencyCode} = price;
        const totalPay = sendAmount + fees - discount;

        return `${submitButton} ${totalPay.toFixed(2)} ${sendCurrencyCode}`;
    }

    handleCancellationLinkClick = event => {
        event.preventDefault();

        if (this.isWaitingForAnalytics) {
            return;
        }

        pushAnalyticsEvent({
            interactionName: 'card payment interaction',
            cardPaymentAction: 'cancel payment',
            eventCallback: () => {
                window.location = this.props.cancellationUrl;
                this.isWaitingForAnalytics = false;
            },
            eventTimeout: 500
        });
        this.isWaitingForAnalytics = true;
    }

    renderExpiryDate(labels) {
        const {featureFlags: {expiryDateDropDown}} = this.props;

        if (expiryDateDropDown.enabled) {
            return (
                <FieldWrapper
                    name={FIELDS.expiryMonth.name}
                    label={labels.expiryDate}
                >
                    <div className="expiry-wrapper-horizontal">
                        <Field {...FIELDS.expiryMonth} />
                        <Field {...FIELDS.expiryYear} />
                    </div>
                </FieldWrapper>
            );
        }
        return (
            <FieldWrapper
                name={FIELDS.expiryDate.name}
                label={labels.expiryDate}
            >
                <Field
                    className="field-wrapper-input"
                    {...FIELDS.expiryDate}
                />
            </FieldWrapper>
        );
    }

    render() {
        const {
            translations,
            isSubmitting,
            formikOptions,
            microformLoaded,
            isWebRelaunchStyleEnabled,
            cancellationUrl,
            shouldSaveCard,
            setShouldSaveCard,
            genericError
        } = this.props;
        const {
            cybersource: {
                labels
            },
            common: {
                cancel
            }
        } = translations;

        return (
            <div>
                {!microformLoaded && <Spinner />}
                <Form className="cybersource-form">
                    <div className="flex-row">
                        <div className="flex-column">
                            <FieldWrapper
                                name={FIELDS.cardNumber.name}
                                label={labels.cardNumber}
                            >
                                <Field
                                    {...FIELDS.cardNumber}
                                    microformField={this.getMicroformField(FIELDS.cardNumber.name)}
                                />
                            </FieldWrapper>
                        </div>
                    </div>

                    <div className="flex-row">
                        <div className="flex-column cybersource-form-column">
                            {this.renderExpiryDate(labels)}
                        </div>
                        <div className="flex-column cybersource-form-column">
                            <FieldWrapper
                                name={FIELDS.securityCode.name}
                                label={labels.securityCode}
                            >
                                <Field
                                    {...FIELDS.securityCode}
                                    translations={translations}
                                    microformField={this.getMicroformField(FIELDS.securityCode.name)}
                                />
                            </FieldWrapper>
                        </div>
                    </div>

                    {config.features.saveCards.enabled && (
                        <div className="container">
                            <label className="switch" htmlFor="checkbox">
                                <input
                                    type="checkbox"
                                    id="checkbox"
                                    onChange={setShouldSaveCard}
                                    checked={shouldSaveCard}
                                    onKeyPress={handleEnterKey(this.props.setShouldSaveCard)}
                                />
                                <div className="slider round" />
                            </label>
                            <p>{translations.cybersource.saveCardForFuture}</p>
                        </div>
                    )}

                    <SubmitButton
                        disabled={!microformLoaded || genericError}
                        isSubmitting={genericError ? false : (isSubmitting || formikOptions.isSubmitting)}
                        showChevron={!isWebRelaunchStyleEnabled}
                    >
                        <span>{this.getSubmitButtonText()}</span>
                    </SubmitButton>
                    <a
                        className="cancel-link"
                        href={cancellationUrl}
                        onClick={this.handleCancellationLinkClick}
                    >
                        {cancel}
                    </a>
                </Form>
            </div>
        );
    }
}

export default CyberSourceFormWrapper;
